import React, { lazy, Suspense, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Navbar, Footer } from '.';
import { CookieBanner } from '../ui';
import AnalyticsContactClickTracker from '../analytics/AnalyticsContactClickTracker';
import AnalyticsRouteTracker from '../analytics/AnalyticsRouteTracker';
import { ScrollNavProvider, useScrollNav } from '../../contexts/scrollNavContext';
import { SiteChromeProvider, useSiteChrome } from '../../contexts/siteChromeContext';
import { useTheme } from '../../contexts/themeContext';
import {
  NAVIGATION_ITEMS,
  LAUNCH_NAVIGATION_ITEMS,
  FOOTER_LINKS,
  LAUNCH_FOOTER_LINKS,
  CONTACT_INFO,
  SOCIAL_LINKS,
  SITE_LAUNCH_MODE,
} from '../../constants';
import {
  SITE_PROJECT_MAIN_PADDING_COLLAPSED,
  SITE_PROJECT_MAIN_PADDING_EXPANDED,
} from '../../constants/siteHeaderStack';

const ChatWidget = lazy(() => import('../ui/ChatWidget'));

const DeferredChatWidget: React.FC = () => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const mount = () => setReady(true);
    const idleCallback = window.requestIdleCallback;

    if (typeof idleCallback === 'function') {
      const id = idleCallback(mount, { timeout: 3000 });
      return () => window.cancelIdleCallback(id);
    }

    const timer = window.setTimeout(mount, 1500);
    return () => window.clearTimeout(timer);
  }, []);

  if (!ready) return null;

  return (
    <Suspense fallback={null}>
      <ChatWidget />
    </Suspense>
  );
};

interface LayoutProps {
  children: React.ReactNode;
  className?: string;
}

function pageShellBackground(pathname: string, isDark: boolean): string {
  const isLaunchHome = SITE_LAUNCH_MODE && pathname === '/';
  const isProjectDetailRoute = /^\/projet\//.test(pathname);
  if (isLaunchHome || isProjectDetailRoute) {
    return 'bg-white';
  }
  return isDark ? 'bg-brand-charcoal' : 'bg-white';
}

const LayoutChrome: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { hideSiteChrome } = useSiteChrome();

  if (hideSiteChrome) {
    return null;
  }

  return <>{children}</>;
};

const LayoutMain: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const { navVisible, isCatalogPage, isProjectDetailPage } = useScrollNav();
  const { hideSiteChrome } = useSiteChrome();

  const needsNavOffset = /^\/(acheter|blog|projet|nous-decouvrir|nos-realisations)/.test(pathname);
  const { isDark } = useTheme();
  const pageShellBg = pageShellBackground(pathname, isDark);

  const [searchParams] = useSearchParams();
  const embedPreview = searchParams.get('embed') === '1';

  let mainPaddingClass = '';
  if (!hideSiteChrome && !embedPreview) {
    if (isCatalogPage) {
      mainPaddingClass = navVisible
        ? 'pt-[11rem] sm:pt-[11.5rem] md:pt-[12.75rem]'
        : 'pt-[5.5rem] sm:pt-[6rem] md:pt-[6.5rem]';
    } else if (isProjectDetailPage) {
      mainPaddingClass = navVisible
        ? SITE_PROJECT_MAIN_PADDING_EXPANDED
        : SITE_PROJECT_MAIN_PADDING_COLLAPSED;
    } else if (needsNavOffset) {
      mainPaddingClass = 'pt-28 md:pt-32';
    }
  }

  return (
    <main
      className={`relative z-[1] ${pageShellBg} ${mainPaddingClass}`}
    >
      {children}
    </main>
  );
};

const Layout: React.FC<LayoutProps> = ({ children, className = '' }) => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const embedPreview = searchParams.get('embed') === '1';
  const { isDark } = useTheme();
  const isProjectDetailRoute = /^\/projet\//.test(pathname);
  const navLogoSrc =
    isProjectDetailRoute || SITE_LAUNCH_MODE || !isDark
      ? '/images/logo-noir.png'
      : '/images/logo-blanc.png';
  const pageShellBg = pageShellBackground(pathname, isDark);
  return (
    <ScrollNavProvider>
    <SiteChromeProvider>
    <div
      className={`relative min-h-screen overflow-x-clip ${pageShellBg} ${className}`}
    >
      {!embedPreview ? (
        <LayoutChrome>
          <Navbar
            logo={
              <div className="flex items-center">
                <img
                  src={navLogoSrc}
                  alt="Provéa, promoteur immobilier"
                  className="h-12 w-auto sm:h-14 md:h-16"
                />
              </div>
            }
            items={SITE_LAUNCH_MODE ? LAUNCH_NAVIGATION_ITEMS : NAVIGATION_ITEMS}
            navigationLocked={SITE_LAUNCH_MODE}
          />
        </LayoutChrome>
      ) : null}

      <LayoutMain>{children}</LayoutMain>

      {!embedPreview ? (
        <>
          <Footer
            links={SITE_LAUNCH_MODE ? LAUNCH_FOOTER_LINKS : FOOTER_LINKS}
            navigationLocked={SITE_LAUNCH_MODE}
            address={{
              street: CONTACT_INFO.address.street,
              city: CONTACT_INFO.address.city,
              postalCode: CONTACT_INFO.address.postalCode,
              phone: CONTACT_INFO.phone,
              email: CONTACT_INFO.email,
            }}
            socialLinks={[
              {
                platform: 'LinkedIn',
                url: SOCIAL_LINKS.linkedin,
                icon: (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                ),
              },
              {
                platform: 'Facebook',
                url: SOCIAL_LINKS.facebook,
                icon: (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                ),
              },
            ]}
          />
          <CookieBanner />
          <AnalyticsRouteTracker />
          <AnalyticsContactClickTracker />
          {!SITE_LAUNCH_MODE ? <DeferredChatWidget /> : null}
        </>
      ) : null}
    </div>
    </SiteChromeProvider>
    </ScrollNavProvider>
  );
};

export default Layout;
